/**
 * The counter: a collector arrives with a TF card, and this is where it is
 * written down (BO-10).
 *
 * **This is an operator's screen on a fixed machine at a staffed counter.** It
 * is not a phone flow and there is no collector-facing version of it: the
 * import runs on the centre's own hardware, the card reader is on that desk,
 * and the person driving this is the clerk, with the collector standing on the
 * other side of the counter. What the flow borrows from a mobile onboarding is
 * only its *shape* — one decision per step, a rail that says where you are, a
 * summary of everything before the commit.
 *
 * **The steps are derived from what the two endpoints accept, and from nothing
 * else.** `POST /handovers` takes a collector, a device, a card label and the
 * moment it was handed over; `POST /handovers/:id/sessions` takes a task, a
 * scenario, a prepare time and the two APP-17b declarations. Three questions
 * about the card, three about the recording, and a summary. Anything not in
 * those two bodies is not a step: the centre, the operator and the machine come
 * off the tokens and are never asked for, and a collection point has a column
 * but no list route to choose one from, so that field is left unset rather than
 * typed as a uuid by a person.
 *
 * Three decisions this screen is built on, all of them recorded in `CLAUDE.md`
 * and none of them re-argued here:
 *
 * - **Session creation is split.** The app binds a session before recording
 *   (APP-16); the operator creates the handover when the card arrives (BO-10).
 *   In the pilot the operator also creates the session, and the server stamps
 *   it `session_origin = 'handover'` so the drift is measurable. The wizard
 *   does not send an origin and cannot.
 * - **There is no `session_ended_at`.** An operator cannot supply a truthful
 *   end, and a retroactively typed end that decides payment attribution is the
 *   failure the brief warns about. No field here asks for one.
 * - **Auto time-matching applies only to `session_origin = 'app'`.** Everything
 *   this screen writes is handover-origin, so its attribution goes to an
 *   operator to confirm rather than being matched against a microsecond PTS
 *   start. The screen says so, and points at where that confirmation happens.
 *
 * And the rule that survives everything: **no TF card is cleared**. Nothing on
 * this path deletes source media, and the last thing the screen says is that
 * the card in the operator's hand is still the card.
 */
import { useCallback, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from '@tanstack/react-router';
import { AppShell } from '../components/shell/AppShell.tsx';
import { DraftRestored } from '../components/ui/DraftRestored.tsx';
import { useDraft } from '../lib/draft.ts';
import { useOperatorProfile } from '../lib/profile-api.ts';
import { Button } from '../components/ui/button.tsx';
import { Panel, Problem, Skeleton } from '../components/ui/primitives.tsx';
import {
  ChoiceList,
  Refusal,
  Wizard,
  WizField,
  YesNo,
  type WizardStep,
} from '../components/wizard/Wizard.tsx';
import {
  IconCamera,
  IconCard,
  IconDeclare,
  IconPerson,
  IconScene,
  IconTask,
} from '../components/icons.tsx';
import { ApiError, counter, type Reference } from '../lib/api.ts';
import { localNow, stampLocal } from '../lib/format.ts';
import { uuid } from '../lib/uuid.ts';
import { intakeActor, pendingDeclarations, saveDeclaration, type IntakeDeclaration } from '../lib/intake.ts';
import { IntakeUpload } from './IntakeUpload.tsx';

export function CounterScreen() {
  const { t } = useTranslation();
  const reference = useQuery({ queryKey: ['counter', 'reference'], queryFn: counter.reference });

  if (reference.error && !(reference.error instanceof ApiError && reference.error.status === 401)) {
    return (
      <AppShell>
        <Header />
        <div className="mt-6">
          <Problem
            title={t('bo.loadFailed')}
            body={t('bo.loadFailed.body')}
            reference={reference.error instanceof ApiError ? reference.error.ref : undefined}
            action={
              <Button variant="outline" size="sm" onClick={() => void reference.refetch()}>
                {t('episodes.reload')}
              </Button>
            }
          />
        </div>
      </AppShell>
    );
  }

  if (reference.isPending || reference.data == null || (reference.error instanceof ApiError && reference.error.status === 401)) {
    return (
      <AppShell>
        <Header />
        <Panel className="mt-6 p-4">
          {[0, 1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="mb-2 h-10 w-full last:mb-0" />
          ))}
        </Panel>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <Header />
      <div className="mt-8">
        <Intake reference={reference.data} />
      </div>
    </AppShell>
  );
}

function Header() {
  const { t } = useTranslation();
  return (
    <header className="border-b border-[var(--foreground)] pb-5">
      <h1 className="headline">
        {t('counter.title')}
      </h1>
      <p className="mt-3 max-w-[64ch] text-[1.0625rem] leading-relaxed text-[var(--muted-foreground)]">
        {t('counter.intro')}
      </p>
    </header>
  );
}

/** What was written, so the done panel can name it without re-reading anything. */
type Recorded = { card: string; collector: string; task: string };

/**
 * What survives leaving the counter screen.
 *
 * Only what the operator typed or picked, plus the two ids they typed it
 * under. No money, no computed figure, and no `done` receipt.
 */
type CounterDraft = {
  handoverId: string;
  sessionId: string;
  batchId: string;
  landed: boolean;
  collectorId: string | null;
  deviceId: string | null;
  card: string;
  handoverAt: string;
  taskId: string | null;
  scenarioId: string | null;
  preparedAt: string;
  others: boolean | null;
  sensitive: boolean | null;
  step: number;
};

function Intake({ reference }: { reference: Reference }) {
  const { t } = useTranslation();

  /**
   * The two ids this intake writes under, minted once and held.
   *
   * Both endpoints are idempotent on a client-generated id, which is the whole
   * of how a counter with the link down retries safely: a submit whose reply
   * was lost has to be retried under the SAME id, and a fresh one on the second
   * click is not a retry, it is a second handover for one card.
   */
  const [handoverId, setHandoverId] = useState(() => uuid());
  const [sessionId, setSessionId] = useState(() => uuid());
  const [batchId, setBatchId] = useState(() => uuid());

  /**
   * The handover row exists.
   *
   * This is the state that makes the difference between the two writes visible.
   * `POST /handovers` is `onConflictDoNothing` and does **not** compare the row
   * it found: sending the same id with a different collector answers
   * `{replayed: true}` and changes nothing, so a wizard that let the first
   * three answers be edited after the handover landed would show one collector
   * on screen and have written another. So once it has landed, those three
   * questions are on the record and are shown rather than asked. The way to
   * correct a wrong card is a new intake, not a second submit under an id the
   * database has already decided.
   */
  const [landed, setLanded] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [done, setDone] = useState<Recorded | null>(null);
  /** Bumped to re-enter the wizard for a second recording on the same card. */
  const [run, setRun] = useState(0);
  const [intakeChanged, setIntakeChanged] = useState(0);
  const [cardLabels, setCardLabels] = useState<Record<string, string>>({});

  const [collectorId, setCollectorId] = useState<string | null>(null);
  const [deviceId, setDeviceId] = useState<string | null>(null);
  const [card, setCard] = useState('');
  const [handoverAt, setHandoverAt] = useState(localNow);

  const [taskId, setTaskId] = useState<string | null>(null);
  const [scenarioId, setScenarioId] = useState<string | null>(null);
  const [preparedAt, setPreparedAt] = useState(localNow);
  const [others, setOthers] = useState<boolean | null>(null);
  const [sensitive, setSensitive] = useState<boolean | null>(null);

  /**
   * The intake, kept so leaving this screen does not lose the counter's work.
   *
   * This is the most dangerous draft in the console and the reason it is the
   * first one built. Everything above is local state, so a reload or a stray
   * click on the top bar threw away nine answers **and minted two fresh ids**.
   * Neither `handovers` nor `collection_sessions` has a natural key — only a
   * primary key and plain indexes — so the second attempt was not a retry: one
   * physical TF card became two handovers, and one handover two sessions.
   * Settlement pays on that attribution.
   *
   * So the two ids travel in the draft, and `landed` with them: it is the only
   * record that the handover half of the commit already succeeded, and an
   * operator who lost it would re-answer the first three questions and send
   * them under a new id. `done` is deliberately absent — it is a receipt for
   * work already written, not work in progress, and restoring it would show a
   * finished intake to somebody who has not done one.
   */
  const [step, setStep] = useState(0);
  const profile = useOperatorProfile();
  const draft = useDraft(
    'counter',
    profile.data?.operator.id,
    {
      handoverId,
      sessionId,
      batchId,
      landed,
      collectorId,
      deviceId,
      card,
      handoverAt,
      taskId,
      scenarioId,
      preparedAt,
      others,
      sensitive,
      step,
    },
    useCallback((held: CounterDraft) => {
      setHandoverId(held.handoverId);
      setSessionId(held.sessionId);
      setBatchId(held.batchId ?? uuid());
      setLanded(held.landed);
      setCollectorId(held.collectorId);
      setDeviceId(held.deviceId);
      setCard(held.card);
      setHandoverAt(held.handoverAt);
      setTaskId(held.taskId);
      setScenarioId(held.scenarioId);
      setPreparedAt(held.preparedAt);
      setOthers(held.others);
      setSensitive(held.sensitive);
      setStep(held.step);
    }, []),
    done === null,
  );

  const collector = reference.collectors.find((c) => c.id === collectorId);
  const device = reference.devices.find((d) => d.id === deviceId);
  const task = reference.tasks.find((x) => x.id === taskId);
  const scenario = reference.scenarios.find((s) => s.id === scenarioId);

  /**
   * The two writes, in order, in one commit.
   *
   * The handover first, because the session hangs off it — and `landed` is set
   * between them rather than at the end, so a session the server refuses
   * leaves the screen telling the truth: the card is recorded, the recording on
   * it is not, and the next commit sends only what is still missing. The
   * handover POST replays harmlessly in that case, which is what its
   * idempotency is for.
   */
  const commit = useMutation({
    mutationFn: async () => {
      const actor = await intakeActor();
      if (actor.operator_id !== profile.data?.operator.id) throw new Error('operator_changed');
      const pending = pendingDeclarations(actor);
      const uncertain = pending.some(row => row.phase === 'committing' && row.sessionId === sessionId);
      if (pending.some(row => row.phase === 'committing' && row.sessionId !== sessionId)) {
        throw new Error('resume_committing_declaration');
      }
      const receipt: IntakeDeclaration = { handoverId, sessionId, batchId, operatorId: actor.operator_id,
        centreId: actor.upload_centre_id, deviceId: actor.upload_device_id, phase: 'committing' };
      saveDeclaration(receipt); // Durable intent BEFORE either network mutation.
      setIntakeChanged(value => value + 1);
      let stage = 'handover';
      try {
        await counter.handover({
          id: handoverId,
          collector_id: collectorId!,
          device_id: deviceId!,
          tf_card_id: card.trim(),
          handover_time: new Date(handoverAt).toISOString(),
        });
        setLanded(true);
        stage = 'session';
        await counter.session(handoverId, {
          id: sessionId,
          task_id: taskId!,
          scenario_id: scenarioId!,
          others_in_frame: others!,
          sensitive_info_present: sensitive!,
          prepare_time: new Date(preparedAt).toISOString(),
        });
        // Save only immutable server IDs before discarding the editable wizard draft.
        // A lost response/storage failure retries these same declaration IDs.
        saveDeclaration({ ...receipt, phase: 'committed' });
        setCardLabels(labels => ({ ...labels, [sessionId]: card.trim() }));
      } catch (failure) {
        // These session refusals follow the route's existing-ID lookup. A generic 4xx,
        // conflict or lost response cannot prove that an earlier attempt did not commit.
        if (failure instanceof ApiError && failure.status === 409 &&
          ((stage === 'session' && failure.message === 'refused' &&
            ['session_claim_missing','session_claim_released','session_task_not_published'].includes(failure.constraint ?? '')) ||
           (stage === 'handover' && !uncertain && failure.message === 'unresolved_reference'))) {
          saveDeclaration({ ...receipt, phase: 'refused' }); setIntakeChanged(value => value + 1);
        }
        throw failure;
      }
    },
    onSuccess: () => {
      setError(null);
      setDone({
        card: card.trim(),
        collector: collector?.externalRef ?? '',
        task: task?.name ?? '',
      });
      /**
       * The intake is written, so it is no longer in progress. Kept, the draft
       * would offer to restore a form whose two ids the database has already
       * decided — a replay that changes nothing, presented as work to finish.
       * `anotherSession` and `anotherCard` mint fresh ids and the draft follows
       * them from there.
       */
      draft.discard();
      setIntakeChanged(value => value + 1);
    },
    onError: setError,
  });

  /** A second recording on the card already on the desk: same handover, new session. */
  const anotherSession = () => {
    setStep(landed ? 3 : 0);
    setSessionId(uuid());
    setBatchId(uuid());
    setTaskId(null);
    setScenarioId(null);
    setPreparedAt(localNow());
    setOthers(null);
    setSensitive(null);
    setDone(null);
    setError(null);
    setRun(run + 1);
  };

  /**
   * "No, throw that away" on a restored draft.
   *
   * It mints fresh ids, and that is the whole point of the button rather than a
   * side effect of reusing `anotherCard`. Keeping the id is right for a
   * *resumed* intake — that is what makes a second submit a retry instead of a
   * second handover. Discarding is the operator saying this is not that intake,
   * so a new action needs a new id; reusing the old one would replay into a row
   * they have just told us they do not want.
   */
  const discardIntake = () => {
    draft.discard();
    anotherCard();
  };

  /** A different collector at the counter: everything starts again. */
  const anotherCard = () => {
    setHandoverId(uuid());
    setSessionId(uuid());
    setLanded(false);
    setCollectorId(null);
    setDeviceId(null);
    setCard('');
    setHandoverAt(localNow());
    anotherSession();
    setStep(0);
  };
  const resumeDeclaration = (receipt: IntakeDeclaration) => {
    draft.discard();
    anotherCard();
    setHandoverId(receipt.handoverId); setSessionId(receipt.sessionId); setBatchId(receipt.batchId);
  };

  const steps: WizardStep[] = [
    {
      id: 'collector',
      group: 'counter.group.card',
      name: 'counter.step.collector',
      question: 'counter.q.collector',
      note: 'counter.note.collector',
      Icon: IconPerson,
      answered: collectorId !== null,
      summary: collector?.externalRef ?? '',
      pane: landed ? (
        <Recorded value={collector?.externalRef ?? ''} />
      ) : (
        <ChoiceList
          name={t('counter.step.collector')}
          items={reference.collectors}
          value={collectorId}
          onChange={setCollectorId}
          idOf={(c) => c.id}
          render={(c) => ({
            title: c.externalRef,
            detail: `${t(`bo.collector.status.${c.status}`)} · ${
              c.examCurrentPassed ? t('bo.collector.currentPass') : t('bo.collector.currentPending')
            } · ${c.agreementsCurrent ? t('bo.collector.consentCurrent') : t('bo.collector.consentPending')}`,
          })}
          empty={<Nothing body={t('counter.empty.collectors')} />}
        />
      ),
    },
    {
      id: 'device',
      group: 'counter.group.card',
      name: 'counter.step.device',
      question: 'counter.q.device',
      note: 'counter.note.device',
      Icon: IconCamera,
      answered: deviceId !== null,
      summary: device?.hardwareSerial ?? '',
      pane: landed ? (
        <Recorded value={device?.hardwareSerial ?? ''} />
      ) : (
        <ChoiceList
          name={t('counter.step.device')}
          items={reference.devices}
          value={deviceId}
          onChange={setDeviceId}
          idOf={(d) => d.id}
          render={(d) => ({
            title: d.hardwareSerial,
            detail: `${t(`bo.device.state.${d.status}`)}${
              d.firmwareVersion === null ? '' : ` · ${d.firmwareVersion}`
            }`,
          })}
          empty={<Nothing body={t('counter.empty.devices')} />}
        />
      ),
    },
    {
      id: 'card',
      group: 'counter.group.card',
      name: 'counter.step.card',
      question: 'counter.q.card',
      note: 'counter.note.card',
      Icon: IconCard,
      answered: card.trim() !== '' && handoverAt !== '',
      summary: `${card.trim()} · ${stampLocal(new Date(handoverAt).toISOString())}`,
      pane: landed ? (
        <Recorded value={`${card.trim()} · ${stampLocal(new Date(handoverAt).toISOString())}`} />
      ) : (
        <div className="grid gap-5 sm:grid-cols-2">
          <WizField
            label={t('counter.field.card')}
            value={card}
            onChange={(e) => setCard(e.target.value)}
            hint={t('counter.hint.card')}
            autoFocus
          />
          <WizField
            label={t('counter.field.handoverAt')}
            type="datetime-local"
            value={handoverAt}
            onChange={(e) => setHandoverAt(e.target.value)}
            hint={t('counter.hint.handoverAt')}
          />
        </div>
      ),
    },
    {
      id: 'task',
      group: 'counter.group.recording',
      name: 'counter.step.task',
      question: 'counter.q.task',
      note: 'counter.note.task',
      Icon: IconTask,
      answered: taskId !== null,
      summary: task?.name ?? '',
      pane: (
        <ChoiceList
          name={t('counter.step.task')}
          items={reference.tasks}
          value={taskId}
          onChange={setTaskId}
          idOf={(x) => x.id}
          render={(x) => ({
            title: x.name,
            /* The unit price as stored, never through Intl: this number
               multiplies into a payment and a rounded copy beside the exact
               one is how two figures start disagreeing. */
            detail: `${t(`bo.task.state.${x.status}`)} · ${x.unitPrice}`,
          })}
          empty={<Nothing body={t('counter.empty.tasks')} />}
        />
      ),
    },
    {
      id: 'scenario',
      group: 'counter.group.recording',
      name: 'counter.step.scenario',
      question: 'counter.q.scenario',
      note: 'counter.note.scenario',
      Icon: IconScene,
      answered: scenarioId !== null && preparedAt !== '',
      summary: `${scenario?.code ?? ''} · ${stampLocal(new Date(preparedAt).toISOString())}`,
      pane: (
        <div className="grid gap-6">
          <ChoiceList
            name={t('counter.step.scenario')}
            items={reference.scenarios}
            value={scenarioId}
            onChange={setScenarioId}
            idOf={(s) => s.id}
            render={(s) => ({
              title: s.code,
              detail: t(`counter.privacy.${s.privacyRiskLevel}`),
            })}
            empty={<Nothing body={t('counter.empty.scenarios')} />}
          />
          <div className="max-w-[22rem]">
            <WizField
              label={t('counter.field.preparedAt')}
              type="datetime-local"
              value={preparedAt}
              onChange={(e) => setPreparedAt(e.target.value)}
              hint={t('counter.hint.preparedAt')}
            />
          </div>
        </div>
      ),
    },
    {
      id: 'declare',
      group: 'counter.group.recording',
      name: 'counter.step.declare',
      question: 'counter.q.declare',
      note: 'counter.note.declare',
      Icon: IconDeclare,
      answered: others !== null && sensitive !== null,
      summary: `${t('counter.declare.others')}: ${answerOf(t, others)} · ${t(
        'counter.declare.sensitive',
      )}: ${answerOf(t, sensitive)}`,
      pane: (
        <div className="grid gap-7">
          <YesNo
            name="others_in_frame"
            label={t('counter.declare.others')}
            value={others}
            onChange={setOthers}
          />
          <YesNo
            name="sensitive_info_present"
            label={t('counter.declare.sensitive')}
            value={sensitive}
            onChange={setSensitive}
          />
        </div>
      ),
    },
  ];

  return (
    <>
      {draft.restoredAt === null ? null : <DraftRestored onDiscard={discardIntake} />}
    <Wizard
      /**
       * Remounted when a draft arrives, because `startAt` is only read on
       * mount. The restore runs in an effect after the first render, so
       * without this the operator would be put back on question one with
       * every answer already filled in behind them. `run` is the same
       * mechanism the second-recording path already uses.
       */
      key={`${run}:${draft.restoredAt ?? 'fresh'}`}
      guide="counter.plan"
      startAt={draft.restoredAt === null ? (landed ? 3 : 0) : step}
      onStepChange={setStep}
      steps={steps}
      title={t('counter.title')}
      intro={t('counter.review.intro')}
      commitLabel={landed ? t('counter.commit.session') : t('counter.commit')}
      committing={commit.isPending}
      onCommit={() => commit.mutate()}
      problem={
        error === null ? undefined : (
          <div className="grid gap-3">
            <Refusal error={error} onDismiss={() => setError(null)} />
            {landed ? (
              <p className="rounded-[var(--radius-base)] border border-[var(--warn)] bg-[var(--warn-bg)] px-4 py-3 text-[0.875rem] leading-relaxed">
                {t('counter.landed')}
              </p>
            ) : null}
          </div>
        )
      }
      done={
        done === null ? undefined : (
          <Done recorded={done} onAnotherSession={anotherSession} onAnotherCard={anotherCard} />
        )
      }
    />
    <IntakeUpload changed={intakeChanged} cardLabels={cardLabels} onResumeDeclaration={resumeDeclaration} />
    </>
  );
}

const answerOf = (t: (k: string) => string, value: boolean | null): string =>
  value === null ? t('wiz.unanswered') : value ? t('counter.declare.yes') : t('counter.declare.no');

/**
 * An answer that is already a row in the database.
 *
 * Shown rather than asked, because the id it was written under is decided and
 * re-sending it changes nothing. Saying that out loud is the difference between
 * a locked field and a broken one.
 */
function Recorded({ value }: { value: string }) {
  const { t } = useTranslation();
  return (
    <div className="rounded-[var(--radius-base)] border border-[var(--border-strong)] bg-[var(--muted)] px-4 py-3.5">
      <p className="num text-[1.0625rem] font-semibold">{value}</p>
      <p className="mt-1.5 max-w-[58ch] text-[0.8125rem] leading-relaxed text-[var(--muted-foreground)]">
        {t('counter.recorded')}
      </p>
    </div>
  );
}

/**
 * A successfully loaded empty list, with a recovery link to the back office.
 * A failed reference sync keeps its separate error state.
 */
function Nothing({ body }: { body: string }) {
  const { t } = useTranslation();
  return (
    <div className="py-6">
      <p className="max-w-[54ch] text-[0.9375rem] leading-relaxed">{body}</p>
      <div className="mt-4">
        <Button asChild variant="secondary" size="sm">
          <Link to="/backoffice">{t('nav.backoffice')}</Link>
        </Button>
      </div>
    </div>
  );
}

/**
 * What was written, and the two things that are still true afterwards.
 *
 * The card is not cleared — that is Rule 6's undeviable half and no code path
 * on this lane touches source media. And the session was recorded at a counter,
 * so its attribution is not matched by time; an operator confirms it, which is
 * what `/episodes` is for. Both sentences are on the screen rather than in a
 * document because the operator holding the card is the person they are about.
 */
function Done({
  recorded,
  onAnotherSession,
  onAnotherCard,
}: {
  recorded: Recorded;
  onAnotherSession: () => void;
  onAnotherCard: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="max-w-[52rem]">
      <p className="text-[0.875rem] font-semibold text-[var(--muted-foreground)]">
        {t('counter.title')}
      </p>
      <h2 className="mt-1 text-[2rem] font-extrabold leading-[1.1] tracking-[-0.03em]">
        {t('counter.done.title')}
      </h2>

      <dl className="mt-6 border-t border-[var(--border)]">
        {[
          [t('counter.step.card'), recorded.card],
          [t('counter.step.collector'), recorded.collector],
          [t('counter.step.task'), recorded.task],
        ].map(([label, value]) => (
          <div
            key={label}
            className="flex items-baseline gap-4 border-b border-[var(--border)] py-3"
          >
            <dt className="w-[11rem] shrink-0 text-[0.8125rem] font-semibold text-[var(--muted-foreground)]">
              {label}
            </dt>
            <dd className="num min-w-0 flex-1 text-[0.9375rem] font-medium">{value}</dd>
          </div>
        ))}
      </dl>

      <p className="mt-6 max-w-[64ch] text-[0.9375rem] leading-relaxed">{t('counter.done.card')}</p>
      <p className="mt-3 max-w-[64ch] text-[0.9375rem] leading-relaxed text-[var(--muted-foreground)]">
        {t('counter.done.match')}
      </p>

      <div className="mt-8 flex flex-wrap gap-3">
        <Button variant="primary" onClick={onAnotherCard}>
          {t('counter.done.nextCard')}
        </Button>
        <Button variant="outline" onClick={onAnotherSession}>
          {t('counter.done.nextSession')}
        </Button>
        <Button asChild variant="ghost">
          <Link to="/episodes">{t('nav.episodes')}</Link>
        </Button>
      </div>
    </div>
  );
}
